import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Column, Row } from "@faire/web/slate/Layout";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import chunk from "lodash/chunk";
import * as React from "react";
import styled from "styled-components";

import { BaseFiniteScrollCarousel } from "@faire/retailer/components/Carousel/FiniteScrollCarousel/BaseFiniteScrollCarousel";
import { Context } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Context";

import { BaseCarouselProps, getTokensForViewEvent } from "../CarouselBase";

interface IProps<T>
  extends BaseCarouselProps,
    Pick<
      BaseFiniteScrollCarousel.IProps,
      "shouldHideRightButton" | "shouldHideLeftButton" | "scrollConfiguration"
    > {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  rows?: number;
}

export const FiniteCarouselBase = <T,>(props: IProps<T>) => {
  const { strictLocalize } = useStrictLocalization();
  const {
    renderItem,
    items,
    rows = 1,
    itemGap,
    useIconArrows,
    iconArrowsOffsetTop,
  } = props;
  const { layoutElement, onVisible, requestId } = React.useContext(Context);

  const [shouldHideRightButton, setShouldHideRightButton] =
    React.useState(false);
  const [shouldHideLeftButton, setShouldHideLeftButton] = React.useState(true);
  const [isOverflow, setIsOverflow] = React.useState(false);
  const elementRef = React.useRef<HTMLDivElement>(null);

  const handleComponentVisible = React.useCallback(() => {
    if (layoutElement.on_click_event_name) {
      /**
       * The maximum length of a token in BE is 255 chars.
       * If you pass a token longer than that, then it will be removed from the event
       * and the event will be recorded without the long token.
       */
      const tokensForViewEvent = getTokensForViewEvent(
        layoutElement,
        strictLocalize
      )
        ?.map((token) => token?.replace(/\s/g, "_"))
        ?.join(" ")
        .slice(0, 255);
      onVisible?.(
        layoutElement.on_click_event_name,
        requestId,
        tokensForViewEvent ?? ""
      );
    }
  }, [layoutElement, onVisible, requestId, strictLocalize]);

  const viewRef = useDoActionWhenVisible(handleComponentVisible);

  React.useEffect(() => {
    if (elementRef.current) {
      setIsOverflow(
        elementRef.current.scrollWidth >
          Math.round(elementRef.current.getBoundingClientRect().width)
      );
    } else {
      setIsOverflow(false);
    }
  }, [elementRef, elementRef.current?.scrollWidth]);

  const itemsPerRow = Math.ceil(items.length / rows);

  if (items.length < 1) {
    return null;
  }

  return (
    <div ref={viewRef}>
      <StyledBaseFiniteScrollCarousel
        forwardRef={elementRef}
        name={layoutElement.on_click_event_name ?? ""}
        shouldHideRightButton={
          props.shouldHideRightButton || !isOverflow || shouldHideRightButton
        }
        shouldHideLeftButton={
          props.shouldHideLeftButton || !isOverflow || shouldHideLeftButton
        }
        onRightEndReached={(isRightEndReached: boolean) => {
          setShouldHideRightButton(isRightEndReached);
        }}
        onLeftEndReached={(isLeftEndReached: boolean) => {
          setShouldHideLeftButton(isLeftEndReached);
        }}
        chevronButtonDimension={{ width: 15, height: 9 }}
        scrollConfiguration={props.scrollConfiguration}
        useIconArrows={useIconArrows}
        iconArrowsOffsetTop={iconArrowsOffsetTop}
      >
        <Column>
          {chunk(items, itemsPerRow).map((itemChunk, chunkIndex) => {
            return (
              <StyledRow
                key={`${layoutElement.on_click_event_name}-row-${chunkIndex}`}
                gap={itemGap}
              >
                {itemChunk.map(renderItem)}
              </StyledRow>
            );
          })}
        </Column>
      </StyledBaseFiniteScrollCarousel>
    </div>
  );
};

const StyledRow = styled(Row).attrs({ align: "flex-start" })`
  &:not(:first-child) {
    margin-top: 16px;
  }
`;

const StyledBaseFiniteScrollCarousel = styled(
  BaseFiniteScrollCarousel.BaseFiniteScrollCarousel
)`
  display: flex;
  flex-direction: row;
  top: 0;
  width: 100%;
  padding: 0;

  ${BaseFiniteScrollCarousel.Previous}, ${BaseFiniteScrollCarousel.Next} {
    color: ${Core.text.enabled};
    width: 30px;
    height: 30px;

    ${responsiveMediaQueries.canHover} {
      color: ${Core.text.subdued};

      &:hover {
        color: ${Core.text.hover};
      }
    }
    ${responsiveMediaQueries.tabletAndBelow} {
      display: none;
    }
  }

  ${BaseFiniteScrollCarousel.Previous} {
    left: -30px;
  }

  ${BaseFiniteScrollCarousel.Next} {
    right: -30px;
  }

  ${BaseFiniteScrollCarousel.Scrollable} {
    align-items: flex-start;
    width: 100%;
  }
`;
